<script setup lang="ts">
export type Props = {
  text?: string
  expiredText?: string
  startDate: Date
  endDate: Date
}

const props = withDefaults(defineProps<Props>(), {
  text: undefined,
  expiredText: undefined,
})

const emit = defineEmits<{
  (event: 'expired'): void
}>()

const progress = ref(0)
const timeLeft = ref('00:00')

const interval = ref<any | null>(null)
const expired = ref(false)

const updateProgress = () => {
  const now = Date.now()
  const start = props.startDate.getTime()
  const end = props.endDate.getTime()
  const total = end - start
  const remaining = end - now

  if (remaining <= 0) {
    if (interval) {
      clearInterval(interval.value)
    }

    progress.value = 100
    timeLeft.value = '00:00'

    expired.value = true

    emit('expired')

    return
  }

  progress.value = (remaining / total) * 100
  timeLeft.value = formatTime(remaining)
}

const formatTime = (time: number) => {
  const minutes = Math.floor(time / 60000)
  const seconds = Math.floor((time % 60000) / 1000)

  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

updateProgress()

onMounted(() => {
  interval.value = setInterval(updateProgress, 1000)
})

onBeforeUnmount(() => {
  clearInterval(interval.value)
})
</script>

<template>
  <div class="app-navbar-timer">
    <div
      class="app-navbar-timer__progress"
      :style="{ transform: `translateX(-${progress}%)` }"
    ></div>

    <p v-if="expired">
      {{ expiredText || $t('component.countdown-timer.expired.description') }}
    </p>

    <p v-else>
      {{ text || $t('component.countdown-timer.description') }}
      <strong class="tabular-nums">{{ timeLeft }}</strong>
    </p>
  </div>
</template>

<style lang="scss">
@import '@boats/ui/styles/config';

.app-navbar-timer {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 1.875rem;
  background-color: $tint-blue;

  p {
    position: relative;
    font-family: $font-barlow;
    font-size: 0.75rem;
    line-height: 1.33333333;
    color: $black;
  }

  strong {
    font-weight: 600;
  }

  @include media-breakpoint-up(md) {
    height: 2.5rem;

    p {
      font-size: 0.875rem;
      line-height: 1.42857143;
    }
  }
}

.app-navbar-timer__progress {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #c0e1ff;
  transform: translateX(-100%);
  @include transition('transform');
}
</style>
